import { z } from 'zod';

/**
 * Wire-format tokens pinned by the messenger-session contract (IMM-1097.1), lowercase on purpose:
 * they are persisted and published verbatim.
 */
export const MESSENGERS = ['whatsapp', 'telegram'] as const;
export const MessengerSchema = z.enum(MESSENGERS);
export type Messenger = z.infer<typeof MessengerSchema>;

/** Closed list of event types carrying `MessengerOutcomePayload`; each caller registers its own. */
export const MESSENGER_OUTCOME_EVENTS = [
  'fleet.question-replied',
  'fleet.report-replied',
  'fleet.notice-settled',
] as const satisfies readonly string[];
export const MessengerOutcomeEventTypeSchema = z.enum(MESSENGER_OUTCOME_EVENTS);
export type MessengerOutcomeEventType = z.infer<typeof MessengerOutcomeEventTypeSchema>;

/** The outcome events whose sessions take a person's text as a discussion with the assistant, not as their reply. */
export const MESSENGER_DISCUSSED_OUTCOME_EVENTS = [
  'fleet.question-replied',
] as const satisfies readonly MessengerOutcomeEventType[];

const MessengerOutcomeBaseSchema = z.object({
  sessionId: z.uuid(),
  messenger: MessengerSchema,
  ref: z.record(z.string(), z.string()),
});

export const MessengerOutcomeReplySchema = z.object({
  body: z.string(),
  senderName: z.string().nullable(),
  receivedAt: z.iso.datetime(),
  /** The inbound message the reply came as, so a handler can acknowledge it. Absent on outcomes written before IMM-1097. */
  providerMessageId: z.string().min(1).nullable().optional(),
});
export type MessengerOutcomeReply = z.infer<typeof MessengerOutcomeReplySchema>;

export const MessengerOutcomePayloadSchema = z.discriminatedUnion('status', [
  MessengerOutcomeBaseSchema.extend({ status: z.literal('replied'), reply: MessengerOutcomeReplySchema }),
  MessengerOutcomeBaseSchema.extend({ status: z.literal('failed'), reply: z.null() }),
]);
export type MessengerOutcomePayload = z.infer<typeof MessengerOutcomePayloadSchema>;
